import { orderBy } from 'lodash-es'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { FvLinkButton, Icon } from '@fv/client-components'

import { FooterBar } from '@/components/FooterBar'
import { List, ListItem } from '@/components/List'

import { DaySelector } from './DaySelector'
import { DayShipmentSelector } from './DayShipmentSelector'
import { DayShipmentSummary } from './DayShipmentSummary'
import { DayStatusNav } from './DayStatusNav'
import { usePrefetchAdjacentLoads, useShipmentsForDay } from './hooks'
import type { CalendarShipmentItem, DayPanelTab } from './types'
import { toCalendarDateKey } from './utils'

const statusPriority: Record<DayPanelTab, number> = {
  scheduled: 1,
  'in-transit': 2,
  delivered: 3,
}

type Props = {
  date: Date | null
  shipments: CalendarShipmentItem[]
  onDateSelected: (date: Date) => void
}

export const CalendarDayPanel = ({
  date,
  shipments,
  onDateSelected,
}: Props) => {
  const shipmentsForDay = useShipmentsForDay(date, shipments)
  const [currentIndex, setCurrentIndex] = useState(0)
  const selectedDateKey = date ? toCalendarDateKey(date) : null
  const sortedShipments = useMemo(
    () => orderBy(shipmentsForDay, s => statusPriority[s.tab]),
    [shipmentsForDay],
  )

  const currentItem = sortedShipments[currentIndex] ?? null
  const activeStatusTab: DayPanelTab = currentItem?.tab ?? 'scheduled'

  const tabShipments = useMemo(
    () => sortedShipments.filter(s => s.tab === activeStatusTab),
    [sortedShipments, activeStatusTab],
  )

  usePrefetchAdjacentLoads(sortedShipments, currentIndex)

  const statusCountsByType = useMemo(() => {
    const counts: Record<DayPanelTab, number> = {
      scheduled: 0,
      'in-transit': 0,
      delivered: 0,
    }

    for (const item of sortedShipments) {
      counts[item.tab] += 1
    }

    return counts
  }, [sortedShipments])

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset back to beginning on day change
  useEffect(() => {
    setCurrentIndex(0)
  }, [selectedDateKey])

  const moveSelection = useCallback(
    (inc: number) => {
      if (sortedShipments.length === 0) return
      const currentItemIndex = sortedShipments.findIndex(
        s => s.loadId === currentItem?.loadId,
      )
      let nextIndex = currentItemIndex + inc

      if (nextIndex < 0 || nextIndex >= sortedShipments.length) {
        return
      }
      setCurrentIndex(nextIndex)
    },
    [sortedShipments, currentItem],
  )

  const handleSelectItem = useCallback(
    (item: (typeof sortedShipments)[number]) => {
      const globalIndex = sortedShipments.findIndex(
        s => s.loadId === item.loadId,
      )
      if (globalIndex !== -1) setCurrentIndex(globalIndex)
    },
    [sortedShipments],
  )

  const handleTabClick = useCallback(
    (tab: DayPanelTab) => {
      const firstIndexWithStatus = sortedShipments.findIndex(
        item => item.tab === tab,
      )
      if (firstIndexWithStatus !== -1) {
        setCurrentIndex(firstIndexWithStatus)
      }
    },
    [sortedShipments],
  )

  return (
    <>
      <div className="p-4 overflow-auto flex flex-col flex-1">
        <DaySelector date={date} onDateSelected={onDateSelected} />

        <DayStatusNav
          activeStatusTab={activeStatusTab}
          statusCountsByType={statusCountsByType}
          onTabClick={handleTabClick}
        />

        <DayShipmentSelector
          shipments={tabShipments}
          currentItem={currentItem}
          onSelect={handleSelectItem}
          onMove={moveSelection}
        />

        {currentItem ? (
          <DayShipmentSummary loadId={currentItem.loadId} />
        ) : (
          <div className="bg-white flex-1 p-4 border border-fv-gray">
            <List className="w-full" fcp0 lcp0>
              <ListItem>
                <Icon icon="dot-circle" className="text-fv-gray/50" />
                <span>No shipments available</span>
              </ListItem>
            </List>
          </div>
        )}
      </div>
      {currentItem && (
        <FooterBar jc>
          <FvLinkButton
            to={`/details/${currentItem.loadId}`}
            icon="arrow-to-right"
          >
            <span>Full details and options for this shipment</span>
          </FvLinkButton>
        </FooterBar>
      )}
    </>
  )
}
